import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
/**
 * Step 1's stage: the animated collection site.
 *
 * Five sites from the script — vehicles on a road, learners with shoe sizes,
 * people being measured, library shelves, sports sign-ups — and the learner
 * clicks each object as it appears to build a tally.
 *
 * The objects are emoji rather than sprites, and that is a decision rather than
 * a placeholder. At the text tier an emoji is the only visual a learner gets,
 * and it is one character over the wire instead of a request; at 2d it renders
 * identically on a five-year-old Android and a desktop, with no asset pipeline
 * and nothing to fail to load. Real art can replace it later by giving the
 * dataset a `backgroundAssetId` and sprite ids — the check does not change.
 */
export function DataCollectionScene({ stage, step, state, tier, dispatch, dataset, disabled, resolveAsset, }) {
    const stepId = step.id;
    const reducedMotion = usePrefersReducedMotion();
    // Which of the five sites is open. Scratch rather than a response: the site
    // a learner is looking at is not part of any answer.
    const selectedId = state.scratch.siteId ?? stage.datasetId;
    const active = dataset(selectedId);
    const tallyChecks = step.checks.filter(check => check.type === 'tally-match');
    const checkForSite = tallyChecks.find(check => check.datasetId === selectedId);
    const counts = (checkForSite ? state.responses[checkForSite.id] : undefined);
    if (!active)
        return _jsx("p", { className: "lab-empty", children: "This step names a data set the lab does not define." });
    const sites = tallyChecks
        .map(check => dataset(check.datasetId))
        .filter((entry) => Boolean(entry));
    return (_jsxs("div", { className: "lab-scene", children: [stage.allowSiteSwitch && sites.length > 1 && (_jsx("div", { className: "lab-scene__sites", role: "tablist", "aria-label": "Data collection sites", children: sites.map(site => {
                    const siteCheck = tallyChecks.find(check => check.datasetId === site.id);
                    const done = siteCheck
                        ? isSiteComplete(site, state.responses[siteCheck.id])
                        : false;
                    return (_jsxs("button", { type: "button", role: "tab", "aria-selected": site.id === selectedId, className: `lab-chip ${site.id === selectedId ? 'is-active' : ''} ${done ? 'is-done' : ''}`, onClick: () => dispatch({ type: 'setScratch', stepId, key: 'siteId', value: site.id }), children: [done ? '✓ ' : '', site.title] }, site.id));
                }) })), _jsx("p", { className: "lab-scene__description", children: active.sceneDescription }), tier === 'text' ? (_jsx(TextScene, { dataset: active })) : (_jsx(VisualScene, { dataset: active, layout: stage.layout, intervalMs: stage.streamIntervalMs, 
                // Reduced motion, not tier, decides whether the traffic flows. A
                // learner who has asked their operating system to stop things moving
                // gets the whole road at once; they are still counting the same
                // forty vehicles.
                animate: !reducedMotion, backgroundUrl: resolveAsset(active.backgroundAssetId), onCount: key => checkForSite &&
                    dispatch({
                        type: 'tally',
                        stepId,
                        checkId: checkForSite.id,
                        categoryKey: key,
                        delta: 1,
                    }), disabled: disabled || stage.mode === 'observe' })), checkForSite && (_jsx(TallyBoard, { dataset: active, counts: counts ?? {}, disabled: disabled, onChange: (key, delta) => dispatch({ type: 'tally', stepId, checkId: checkForSite.id, categoryKey: key, delta }) }))] }));
}
/**
 * Reads `prefers-reduced-motion` and follows it if it changes.
 *
 * It changes more often than you would think: a learner who switches it on
 * because the traffic is making them queasy should see the road settle without
 * reloading the lab and losing their tally.
 */
function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(() => typeof window !== 'undefined'
        ? (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
        : false);
    useEffect(() => {
        const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        if (!query)
            return;
        const listener = (event) => setReduced(event.matches);
        query.addEventListener('change', listener);
        return () => query.removeEventListener('change', listener);
    }, []);
    return reduced;
}
function isSiteComplete(dataset, counts) {
    if (!counts)
        return false;
    return dataset.categories.every(category => counts[category.key] === category.count);
}
/**
 * The text tier's scene.
 *
 * A described list rather than an animation. It carries exactly the information
 * the animation carries — which categories exist and what they look like — and
 * the learner still has to enter the counts themselves, from the objects listed
 * below. The check is byte-identical to the one at 2d.
 */
function TextScene({ dataset }) {
    return (_jsxs("div", { className: "lab-scene__text", children: [_jsx("p", { className: "lab-hint", children: "You are seeing the light version of this scene. The categories and the objects to count are listed below \u2014 the questions are exactly the same as on a faster connection." }), _jsx("ul", { className: "lab-scene__list", children: dataset.categories.map(category => (_jsxs("li", { children: [_jsx("span", { "aria-hidden": "true", children: category.icon ?? '•' }), " ", category.label] }, category.key))) })] }));
}
/**
 * The 2d scene.
 *
 * `stream` releases objects one at a time on an interval — the script's
 * "let the objects flow periodically in a natural set-up" — and `grid` lays
 * them all out at once, which is what shoes on a rack and books on a shelf
 * actually look like.
 */
function VisualScene({ dataset, layout, intervalMs, animate, backgroundUrl, onCount, disabled, }) {
    // The objects, shuffled deterministically from the dataset id so that every
    // learner at the same site sees the same order. A random shuffle would make
    // "I counted eight vans" unreproducible when a teacher tries to help.
    const objects = useMemo(() => buildObjects(dataset), [dataset]);
    const [revealed, setRevealed] = useState(layout === 'stream' && animate ? 0 : objects.length);
    const [clicked, setClicked] = useState(() => new Set());
    useEffect(() => {
        if (layout !== 'stream' || !animate) {
            setRevealed(objects.length);
            return;
        }
        setRevealed(0);
        const timer = setInterval(() => {
            setRevealed(current => {
                if (current >= objects.length) {
                    clearInterval(timer);
                    return current;
                }
                return current + 1;
            });
        }, intervalMs);
        return () => clearInterval(timer);
    }, [objects, layout, intervalMs, animate]);
    return (_jsxs("div", { className: `lab-scene__stage lab-scene__stage--${layout}`, children: [backgroundUrl && (
            // Graceful: a pack declares where the media should live, and an
            // administrator uploads it later. Until then the scene is the emoji,
            // which is playable rather than broken.
            _jsx("img", { className: "lab-scene__background", src: backgroundUrl, alt: "", "aria-hidden": "true", onError: event => {
                    event.currentTarget.style.display = 'none';
                } })), _jsx("div", { className: "lab-scene__objects", children: objects.slice(0, revealed).map((object, index) => (_jsx("button", { type: "button", disabled: disabled || clicked.has(index), className: `lab-object ${clicked.has(index) ? 'is-counted' : ''}`, style: object.color ? { borderColor: object.color } : undefined, "aria-label": `Count one ${object.label}`, onClick: () => {
                        setClicked(current => new Set(current).add(index));
                        onCount(object.key);
                    }, children: _jsx("span", { "aria-hidden": "true", children: object.icon }) }, index))) }), revealed < objects.length && (_jsxs("p", { className: "lab-scene__progress", "aria-live": "polite", children: [revealed, " of ", objects.length, " have appeared\u2026"] }))] }));
}
function buildObjects(dataset) {
    const objects = [];
    for (const category of dataset.categories) {
        for (let index = 0; index < category.count; index += 1) {
            objects.push({
                key: category.key,
                label: category.label,
                icon: category.icon ?? '⬤',
                color: category.color,
            });
        }
    }
    return interleave(objects, dataset.id);
}
/**
 * A deterministic shuffle.
 *
 * Seeded from the dataset id, so the traffic looks mixed rather than sorted by
 * type, and looks the *same* mixed order for every learner. Reproducibility
 * matters more here than entropy: a teacher helping a learner who says "I got
 * nine buses" needs to be looking at the same road.
 */
function interleave(objects, seed) {
    let hash = 0;
    for (let index = 0; index < seed.length; index += 1) {
        hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
    }
    const result = [...objects];
    for (let index = result.length - 1; index > 0; index -= 1) {
        hash = (hash * 1103515245 + 12345) >>> 0;
        const swap = hash % (index + 1);
        [result[index], result[swap]] = [result[swap], result[index]];
    }
    return result;
}
/** The running tally, and the only way to enter counts at the text tier. */
function TallyBoard({ dataset, counts, disabled, onChange, }) {
    return (_jsxs("table", { className: "lab-table lab-tally", children: [_jsxs("caption", { children: [dataset.title, " \u2014 your tally"] }), _jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { scope: "col", children: dataset.categoryLabel }), _jsx("th", { scope: "col", children: "Count" }), _jsx("th", { scope: "col", children: _jsx("span", { className: "lab-visually-hidden", children: "Adjust" }) })] }) }), _jsx("tbody", { children: dataset.categories.map(category => (_jsxs("tr", { children: [_jsxs("th", { scope: "row", children: [_jsxs("span", { "aria-hidden": "true", children: [category.icon, " "] }), category.label] }), _jsx("td", { className: "lab-tally__count", children: counts[category.key] ?? 0 }), _jsx("td", { children: _jsxs("div", { className: "lab-stepper", children: [_jsx("button", { type: "button", disabled: disabled, "aria-label": `One fewer ${category.label}`, onClick: () => onChange(category.key, -1), children: "\u2212" }), _jsx("button", { type: "button", disabled: disabled, "aria-label": `One more ${category.label}`, onClick: () => onChange(category.key, 1), children: "+" })] }) })] }, category.key))) }), _jsx("tfoot", { children: _jsxs("tr", { children: [_jsx("th", { scope: "row", children: "Total" }), _jsx("td", { className: "lab-tally__count", children: dataset.categories.reduce((sum, category) => sum + (counts[category.key] ?? 0), 0) }), _jsx("td", {})] }) })] }));
}
//# sourceMappingURL=DataCollectionScene.js.map